import openSuccessBannerAction from 'modules/banner/actions/open-success'
import {
  getDomainListDataPathCommentsUtil,
  getDomainListPathCommentsUtil,
  getNewCommentsUtil,
  getNewPathCommentsUtil,
  getPathCommentsUtil
} from 'modules/comments/utils'
import getCurrentUserSelector from 'modules/users/selectors/get-current'
import { COMMENTS_PATH } from 'modules/comments/constants'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import {
  COMMENT_MODEL_CLASS_NAME,
  COMMENT_MODEL_KEY
} from 'models/daos/comment/constants'
import isNewReduxUtil from 'modules/redux/utils/is-new'
import { updateLegacyReduxAction } from 'modules/redux/actions/legacy/update'
import { validateLegacyReduxAction } from 'modules/redux/actions/legacy/validate'
import { deleteLegacyReduxAction } from 'modules/redux/actions/legacy/delete'
import { saveLegacyReduxAction } from 'modules/redux/actions/legacy/save'
import { discardLegacyReduxAction } from 'modules/redux/actions/legacy/discard'
import { deletedMessage } from 'messages'
import { getValidationReduxUtil } from 'modules/redux/utils/legacy/get-validation'
import {
  createPointerReduxUtil,
  createReduxUtil
} from 'modules/redux/utils/legacy/create'
import { getObjectUtil } from 'packages/src'

/**
 * Adds a new `Comment` to an existing comment.
 * @param  {number} id
 * @param  {Article|Comment|Motivation} associatedInstance
 * @param  {Article|Motivation} rootInstance
 * @returns {Function}
 */
function createCommentAction (id, associatedInstance, rootInstance) {
  return (dispatch, getState) => {
    // If there is already a new reply for another `Comment`, create it
    let newComment = rootInstance
      ? getNewCommentsUtil(getState().comments, rootInstance)
      : null
    let newCommentId = getIdReduxUtil(newComment)
    const commentsData = getObjectUtil(getState(), COMMENTS_PATH.DATA)
    const commentIds = Object.keys(commentsData)
    let replyCommentId = commentIds.find(
      commentId => commentId < 0 && commentId !== newCommentId
    )
    replyCommentId = ~~replyCommentId
    const commentDomainListData = getObjectUtil(
      getState().comments, `comments.${id}.data`
    ) || []
    const hasAlreadyAReply = commentDomainListData.includes(replyCommentId)
    if (hasAlreadyAReply) {
      return
    }
    if (replyCommentId) {
      dispatch(
        deleteLegacyReduxAction(replyCommentId, associatedInstance)
      )
    }

    // Create the new `Comment`
    const user = getCurrentUserSelector()(getState())
    newComment = createReduxUtil(COMMENT_MODEL_CLASS_NAME, {
      changed: { [COMMENT_MODEL_KEY.VALUE]: '' },
      data: { user: createPointerReduxUtil(user) }
    })
    newCommentId = getIdReduxUtil(newComment)
    const path = getPathCommentsUtil(newCommentId)
    // Add the new `Comment` to the `DomainMap`
    dispatch(
      updateLegacyReduxAction({
        path,
        value: newComment
      })
    )
    // If the `associatedInstance` is not a `Comment` add a reply to it
    if (associatedInstance.className !== COMMENT_MODEL_CLASS_NAME) {
      dispatch(
        addReply(id, newComment)
      )
    } else {
      // If the `associatedInstance` is a `Comment`, add a reply to the main `Comment`
      // Only the main `Comment` is allowed to have replies
      dispatch(
        addReplyToMainComment(id, newComment, associatedInstance)
      )
    }
  }
}

/**
 * Adds a `Comment` to an existing `Comment`.
 * @private
 * @param  {number} currentCommentId
 * @param  {Comment} newComment
 * @returns {Function}
 */
function addReply (currentCommentId, newComment) {
  return (dispatch, getState) => {
    // Get the current `Comment`
    const currentComment = getObjectUtil(getState(), getPathCommentsUtil(currentCommentId))
    // Update the current `Comment`'s `DomainMap`
    const path = getDomainListDataPathCommentsUtil(currentComment)
    let value = getObjectUtil(getState(), path) || []
    const id = getIdReduxUtil(newComment)
    value = [
      ...value,
      id
    ]
    dispatch(
      updateLegacyReduxAction({
        path,
        value
      })
    )
  }
}

/**
 * Adds a `Comment` to the existing `mainComment`.
 * It also mentions the `User` of the `Comment` with the id
 * `currentCommentId`.
 * @private
 * @param  {number} currentCommentId
 * @param  {Comment} newComment
 * @param  {Comment} mainComment
 * @returns {Function}
 */
function addReplyToMainComment (currentCommentId, newComment, mainComment) {
  return (dispatch, getState) => {
    // Get the current `Comment`'s `User`'s name
    let path = getPathCommentsUtil(currentCommentId)
    const currentComment = getObjectUtil(getState(), path)
    const currentCommentUsername = getObjectUtil(currentComment, 'data.user.data.name')
    // Mention the `Comment`'s `User` in the `newComment`
    const key = COMMENT_MODEL_KEY.VALUE
    const newCommentId = getIdReduxUtil(newComment)
    path = getPathCommentsUtil(newCommentId)
    let value = `@${currentCommentUsername} `
    dispatch(
      updateLegacyReduxAction({
        path,
        key,
        value
      })
    )
    // Update the `mainComment`'s `DomainMap`
    path = getDomainListDataPathCommentsUtil(mainComment)
    value = getObjectUtil(getState(), path) || []
    const id = getIdReduxUtil(newComment)
    value = [
      ...value,
      id
    ]
    dispatch(
      updateLegacyReduxAction({
        path,
        value
      })
    )
  }
}

/**
 * Updates the Comment with the given `id`.
 * If the comment doesn't exist, it creates it
 *
 * NOTE: Creates the `Comment` if it doesn't exists.
 *
 * @param  {number} id
 * @param  {string} value
 * @param  {Instance} associatedInstance
 * @returns {Function}
 */
function updateCommentAction (id, value, associatedInstance) {
  return (dispatch, getState) => {
    let path
    // If the `Comment` doesn't exist, create it
    if (!id) {
      // Create the `Comment`
      let user = getCurrentUserSelector()(getState())
      user = createPointerReduxUtil(user)
      const data = { user }
      const className = COMMENT_MODEL_CLASS_NAME
      const comment = createReduxUtil(className, { data })
      id = getIdReduxUtil(comment)
      path = getPathCommentsUtil(id)
      // Add the `Comment` to the `DomainMap`
      dispatch(updateLegacyReduxAction({
        path,
        value: comment
      }))
      // Add the Comment id to the domainMap
      const domainMapPath = getDomainListPathCommentsUtil(associatedInstance)
      const domainMap = (
        getObjectUtil(getState(), domainMapPath) || {}
      )
      dispatch(updateLegacyReduxAction({
        path: domainMapPath,
        value: {
          ...domainMap,
          new: id
        }
      }))
    }
    // Update the comment
    path = path || getPathCommentsUtil(id)
    const key = COMMENT_MODEL_KEY.VALUE
    dispatch(updateLegacyReduxAction({
      path,
      value,
      key
    }))
  }
}

/**
 * Saves the `Comment`.
 * Show a success message if it's a new `Comment`.
 * @param  {number} id
 * @param  {Instance} associatedInstance
 * @returns {Function}
 */
function saveCommentAction (id, associatedInstance) {
  return (dispatch, getState) => {
    // Get the Comment
    const path = getPathCommentsUtil(id)
    // Validate the Comment
    dispatch(validateLegacyReduxAction(path))
    // If the Comment is invalid, noop
    const comment = getObjectUtil(getState(), path)
    if (getValidationReduxUtil(comment) !== true) {
      return
    }
    // Get the `associatedInstance` `id` and `className`
    const associatedId = getObjectUtil(associatedInstance, 'data.id')
    const associatedClassName = getObjectUtil(associatedInstance, 'className')
    const additionalData = {
      associatedId,
      associatedClassName
    }
    // so the default `domainListPath` won't be used
    let domainListPath = null
    // Add the new `Comment` to the `associatedInstance`
    if (isNewReduxUtil(comment)) {
      domainListPath = getDomainListPathCommentsUtil(associatedInstance)
    }
    // Save the comment
    const promise = dispatch(
      saveLegacyReduxAction({
        path,
        additionalData,
        domainListPath
      })
    )
    // When the comment is saved, reset the domain new
    if (isNewReduxUtil(comment) && promise) {
      return promise.then(() => {
        dispatch(reset(id, associatedInstance))
      })
    }
    return promise
  }
}

/**
 * Removes the new Comment from the DomainMap.
 * Also removes it from the DominList of the given `associatedInstance`.
 * @private
 * @param  {number} id
 * @param  {Instance} associatedInstance
 * @returns {Function}
 */
function reset (id, associatedInstance) {
  return (dispatch, getState) => {
    // Delete the comment from its `associatedInstance`
    let path = getNewPathCommentsUtil(associatedInstance)
    const newId = getObjectUtil(getState(), path)
    // If it has a DomainNew, remove it from there
    // Used for writing a new comment instead of replying to an
    // existing one
    if (newId) {
      dispatch(updateLegacyReduxAction({
        path
      }))
    } else {
      // Remove it from the DomainList data
      // Used for replying to existing comments
      // Get the DomainList data
      path = getDomainListDataPathCommentsUtil(associatedInstance)
      let domainListData = getObjectUtil(getState(), path)
      // Remove the id from the DomainList data
      domainListData = domainListData.filter(i => i !== id)
      dispatch(updateLegacyReduxAction({
        path,
        value: domainListData
      }))
    }
    // Delete the comment with the given id
    path = getPathCommentsUtil(id)
    dispatch(updateLegacyReduxAction({
      path
    }))
  }
}

/**
 * Discards the change on the comment with the given `id`.
 * If the comment is new, it gets deleted.
 *
 * @param  {number} id
 * @param  {Instance} associatedInstance
 * @returns {Function}
 */
function discardCommentAction (id, associatedInstance) {
  return (dispatch, getState) => {
    // Get the comment
    const path = getPathCommentsUtil(id)
    const instance = getObjectUtil(getState(), path)
    const isNew = isNewReduxUtil(instance)
    // Delete the instance
    if (isNew) {
      dispatch(reset(id, associatedInstance))
    } else {
      // Discard the changes
      dispatch(
        discardLegacyReduxAction(path)
      )
    }
  }
}

/**
 * Delete the `Comment` remotely.
 * Removes the `Comment` from the `DomainMap` and its associated instance.
 *
 * @param  {number} id
 * @param  {Instance} associatedInstance
 * @returns {Function}
 */
function delCommentAction (id, associatedInstance) {
  return (dispatch, getState) => {
    // Get the Comment Path
    const path = getPathCommentsUtil(id)
    const comment = getObjectUtil(getState(), path)
    const value = comment.data.value || comment.data.changed
    // Delete the Comment
    const domainListPath = getDomainListPathCommentsUtil(
      associatedInstance
    )
    // Get the associated instance's `id` and `className`
    const associatedId = getObjectUtil(associatedInstance, 'data.id')
    const associatedClassName = getObjectUtil(associatedInstance, 'className')
    const additionalData = {
      associatedId,
      associatedClassName
    }
    const promise = dispatch(
      deleteLegacyReduxAction({
        path,
        domainListPath,
        additionalData
      })
    )
    if (promise) {
      promise.then(() => dispatch(onAfterDeleteAction(value)))
    } else {
      dispatch(onAfterDeleteAction(value))
    }
  }
}

function onAfterDeleteAction () {
  return openSuccessBannerAction(deletedMessage(COMMENT_MODEL_CLASS_NAME))
}

export {
  createCommentAction,
  updateCommentAction,
  saveCommentAction,
  discardCommentAction,
  delCommentAction
}
